import { inject, Injectable } from '@angular/core'
import { AssetClassEnum } from '@modules/positions-module/enums/asset-class.enum'
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'
import { WebsocketService } from '@modules/websocket-module/services/websocket.service'
import { StockOrderMessageInterface } from '@modules/stocks-module/orders/interfaces/stock-order-message.interface'
import { OrderInterface } from '@modules/orders-module/interfaces/order.interface'
import { EnvironmentsHelperService } from '@environments/core/services/environments-helper.service'
import { OrderStateEnum } from '@modules/orders-module/enums/order-state.enum'
import { OrderTypeEnum } from '@modules/orders-module/enums/order-type.enum'
import {
	BehaviorSubject,
	catchError,
	distinctUntilChanged,
	EMPTY,
	filter,
	map,
	Observable,
	of,
	switchMap,
	tap,
	throwError,
} from 'rxjs'
import { OrdersResponseInterface } from '@modules/orders-module/interfaces/order-response.interface'
import { OrderParamsInterface } from '@modules/orders-module/interfaces/order-params.interface'
import { UserDetailInterface } from '@modules/user-module/interfaces/user-detail.interface'
import { ChangedSimpleOrderInterface } from '@modules/orders-module/interfaces/changed-simple-order.interface'
import { ChangeComplexOrderInterface } from '@modules/stocks-module/orders/interfaces/changed-complex-order.interface'
import { CreateOrderInterface } from '@modules/orders-module/interfaces/create-order.interface'
import { StocksOrdersHelperService } from '@modules/stocks-module/orders/services/stocks-orders-helper.service'
import { UtilsService } from '@shared/services/utils.service'
import { TrailOrderSocketResponseInterface } from '@modules/stocks-module/orders/interfaces/trail-order-socket-response.interface'

@Injectable({
	providedIn: 'root',
})
export class StocksOrdersService {
	protected readonly assetClassEnum: typeof AssetClassEnum = AssetClassEnum
	protected readonly orderStateEnum: typeof OrderStateEnum = OrderStateEnum
	protected readonly orderTypeEnum: typeof OrderTypeEnum = OrderTypeEnum

	private readonly _http: HttpClient = inject<HttpClient>(HttpClient)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)
	private readonly _ordersStocksHelperService: StocksOrdersHelperService =
		inject<StocksOrdersHelperService>(StocksOrdersHelperService)
	private readonly _websocketService: WebsocketService<StockOrderMessageInterface, OrderInterface> =
		inject<WebsocketService<StockOrderMessageInterface, OrderInterface>>(WebsocketService)
	private readonly _environmentsHelperService: EnvironmentsHelperService =
		inject<EnvironmentsHelperService>(EnvironmentsHelperService)
	private readonly _utilsService: UtilsService = inject<UtilsService>(UtilsService)

	public getAllOrders(state: OrderStateEnum): Observable<OrdersResponseInterface> {
		const ordersParams: OrderParamsInterface | undefined = this._ordersStocksHelperService.ordersParamsMap
			.get(state)
			?.getValue()
		if (!ordersParams) return of()

		const params: HttpParams = new HttpParams()
			.set('status', ordersParams.status)
			.set('page', ordersParams.page)
			.set('page_size', ordersParams.pageSize)

		this._ordersStocksHelperService.toggleLoadersOrders(state, true)
		return this._http
			.get<OrdersResponseInterface>(`${this._environmentsHelperService.apiUrl}/orders/order_history/`, { params })
			.pipe(
				tap((response: OrdersResponseInterface): void => {
					this._ordersStocksHelperService.setOrdersResponseByState(response, state)
					this._ordersStocksHelperService.toggleLoadersOrders(state, false)
				}),
				catchError((error: HttpErrorResponse): never => {
					this._ordersStocksHelperService.toggleLoadersOrders(state, false)
					throw error
				}),
			)
	}

	public launchSocketOrders(): Observable<OrderInterface> {
		const userDetail: UserDetailInterface | null = this._userHelperService.userDetail$.getValue()
		if (!userDetail) return of()

		const url: string = `${this._environmentsHelperService.socketUrl}/ws/user_orders_v3/`

		this._websocketService.connect(url)

		if (!this._websocketService.hasMessageInSocket(url)) {
			this._websocketService.sendMessage(url, {
				action: 'get_orders',
				request_id: this._utilsService.generateUniqId(),
				account_id: userDetail.broker_id,
			})
		}

		return this._websocketService.getResponses(url).pipe(
			filter((order: OrderInterface): boolean => order.asset_class === this.assetClassEnum.US_EQUITY),
			map((order: OrderInterface): OrderInterface => this._ordersStocksHelperService.formatOrderBySocket(order)),
			tap((order: OrderInterface): void => {
				this._ordersStocksHelperService.moveOrder(order)
			}),
			catchError((error: HttpErrorResponse): never => {
				throw error
			}),
		)
	}

	public changeSimpleOrder(order: ChangedSimpleOrderInterface): Observable<OrderInterface> {
		if (this._userHelperService.checkIsLimited()) return throwError((): null => null)

		return this._http
			.patch<OrderInterface>(`${this._environmentsHelperService.apiUrl}/orders/replace_order/`, order)
			.pipe(
				tap((response: OrderInterface): void => {
					this._ordersStocksHelperService.moveOrder(response)
				}),
			)
	}

	public changeComplexOrder(order: ChangeComplexOrderInterface): Observable<OrderInterface> {
		if (this._userHelperService.checkIsLimited()) return throwError((): null => null)

		return this._http
			.patch<OrderInterface>(`${this._environmentsHelperService.apiUrl}/orders/replace_advanced_order/`, order)
			.pipe(
				tap((response: OrderInterface): void => {
					this._ordersStocksHelperService.moveOrder(response)
				}),
			)
	}

	public createOrder(order: CreateOrderInterface): Observable<OrderInterface> {
		if (this._userHelperService.checkIsLimited()) return throwError((): null => null)

		return this._http
			.post<OrderInterface>(`${this._environmentsHelperService.apiUrl}/orders/create_order/`, order)
			.pipe(
				tap((response: OrderInterface): void => {
					this._ordersStocksHelperService.moveOrder(response)
				}),
			)
	}

	public cancelOrder(orderId: string): Observable<OrderInterface> {
		return this._http
			.post<OrderInterface>(`${this._environmentsHelperService.apiUrl}/orders/cancel_order/`, {
				order_id: orderId,
			})
			.pipe(
				tap((response: OrderInterface): void => {
					this._ordersStocksHelperService.moveOrder(response)
					this._ordersStocksHelperService.removeOpenedOrder(response)
				}),
			)
	}

	public launchSocketTrailOrders(): Observable<TrailOrderSocketResponseInterface[]> {
		const userDetail: UserDetailInterface | null = this._userHelperService.userDetail$.getValue()
		if (!userDetail) return of()

		const url: string = `${this._environmentsHelperService.socketUrl}/ws/user_open_trail_orders/`
		const openOrders$: BehaviorSubject<OrdersResponseInterface | null> | undefined =
			this._ordersStocksHelperService.ordersResponseMap.get(this.orderStateEnum.OPEN)
		if (!openOrders$) return of()

		return openOrders$.pipe(
			map(
				(response: OrdersResponseInterface | null): boolean =>
					!!response?.orders.some(
						(order: OrderInterface): boolean => order.type === this.orderTypeEnum.TRAILING_STOP,
					),
			),
			distinctUntilChanged(),
			switchMap((hasTrailingStop: boolean): Observable<TrailOrderSocketResponseInterface[]> => {
				if (!hasTrailingStop) {
					this._websocketService.disconnect(url)
					return EMPTY
				}

				this._websocketService.connect(url)
				if (!this._websocketService.hasMessageInSocket(url)) {
					this._websocketService.sendMessage(url, {
						action: 'get_orders',
						request_id: this._utilsService.generateUniqId(),
						account_id: userDetail.broker_id,
					})
				}

				return (
					this._websocketService.getResponses(url) as unknown as Observable<
						TrailOrderSocketResponseInterface[]
					>
				).pipe(
					tap((orders: TrailOrderSocketResponseInterface[]): void => {
						this._ordersStocksHelperService.updateTrailOrdersStopPrice(orders)
					}),
					catchError((error: HttpErrorResponse): never => {
						throw error
					}),
				)
			}),
		)
	}
}
